import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  FileSignature, Search, RefreshCw, Filter, Send, Eye, 
  CheckCircle2, Clock, Mail, XCircle, ExternalLink, AlertCircle
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentESignatures() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Dialog states
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(null);
  const [resending, setResending] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/agent/signature-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load signature requests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  const fetchStats = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/signature-requests/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRequests();
  };

  const handleViewDetails = async (request) => {
    setDetailsOpen(true);
    setLoadingDetails(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/signature-requests/${request.request_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRequest(response.data);
    } catch (error) {
      toast.error("Failed to load request details");
      setDetailsOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCheckStatus = async (requestId) => {
    setCheckingStatus(requestId);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/signature-requests/${requestId}/check-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status: ${response.data.status}`);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check status");
    } finally {
      setCheckingStatus(null);
    }
  };

  const handleResend = async (requestId) => {
    setResending(requestId);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/signature-requests/${requestId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Signature request resent successfully");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend request");
    } finally {
      setResending(null);
    }
  };

  const handleCancel = async () => {
    if (!selectedRequest) return;
    setCancelling(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/signature-requests/${selectedRequest.request?.request_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Signature request cancelled");
      setCancelOpen(false);
      setDetailsOpen(false);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { class: "bg-yellow-100 text-yellow-800", icon: Clock },
      sent: { class: "bg-blue-100 text-blue-800", icon: Mail },
      viewed: { class: "bg-purple-100 text-purple-800", icon: Eye },
      signed: { class: "bg-green-100 text-green-800", icon: CheckCircle2 },
      expired: { class: "bg-gray-100 text-gray-800", icon: AlertCircle },
      cancelled: { class: "bg-red-100 text-red-800", icon: XCircle }
    };
    const cfg = config[status] || config.pending;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.class} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status || "Unknown"}
      </Badge>
    );
  };

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
          E-Signatures
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage document signing requests via DocuSeal
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="card-organic cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter(""); fetchRequests(); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <FileSignature className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("pending"); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-yellow-100">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("sent"); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.sent}</p>
                  <p className="text-xs text-muted-foreground">Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("viewed"); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100">
                  <Eye className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.viewed}</p>
                  <p className="text-xs text-muted-foreground">Viewed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("signed"); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.signed}</p>
                  <p className="text-xs text-muted-foreground">Signed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("cancelled"); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.expired + stats.cancelled || 0}</p>
                  <p className="text-xs text-muted-foreground">Expired/Cancelled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filter */}
      <Card className="card-organic mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by policy ID, name, email, or account number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-signatures-input"
                />
              </div>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="viewed">Viewed</SelectItem>
                <SelectItem value="signed">Signed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button type="button" variant="outline" onClick={() => { setSearch(""); setStatusFilter(""); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Signature Requests Table */}
      <Card className="card-organic">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
            Signature Requests ({requests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSignature className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No signature requests found</p>
              <p className="text-sm mt-2">Send a policy contract for signature from the Policy Contracts page</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Policy ID</th>
                    <th className="text-left p-3 font-medium">Customer</th>
                    <th className="text-left p-3 font-medium">Account #</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Sent Date</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req) => (
                    <tr key={req.request_id} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <code className="text-sm bg-muted px-2 py-1 rounded">{req.policy_id}</code>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{req.user_name || "N/A"}</p>
                          <p className="text-sm text-muted-foreground">{req.user_email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <code className="text-sm text-primary font-medium">{req.account_number || "N/A"}</code>
                      </td>
                      <td className="p-3">{getStatusBadge(req.status)}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {req.created_at ? new Date(req.created_at).toLocaleDateString() : "N/A"}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewDetails(req)}
                            data-testid={`view-${req.request_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleCheckStatus(req.request_id)}
                            disabled={checkingStatus === req.request_id || req.status === "signed"}
                            data-testid={`check-${req.request_id}`}
                          >
                            {checkingStatus === req.request_id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </Button>
                          {req.status !== "signed" && req.status !== "cancelled" && (
                            <Button 
                              size="sm"
                              variant="secondary"
                              onClick={() => handleResend(req.request_id)}
                              disabled={resending === req.request_id}
                              data-testid={`resend-${req.request_id}`}
                            >
                              {resending === req.request_id ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Signature Request Details</DialogTitle>
          </DialogHeader>
          {loadingDetails ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : selectedRequest ? (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedRequest.request?.status === "signed" ? "bg-green-50 border border-green-200" :
                selectedRequest.request?.status === "viewed" ? "bg-purple-50 border border-purple-200" :
                selectedRequest.request?.status === "cancelled" ? "bg-red-50 border border-red-200" :
                "bg-blue-50 border border-blue-200"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusBadge(selectedRequest.request?.status)}
                    <span className="text-sm text-muted-foreground">
                      {selectedRequest.request?.status === "signed" && selectedRequest.request?.signed_at ? (
                        `Signed on ${new Date(selectedRequest.request.signed_at).toLocaleString()}`
                      ) : (
                        `Sent on ${new Date(selectedRequest.request?.created_at).toLocaleString()}`
                      )}
                    </span>
                  </div>
                  {selectedRequest.request?.signing_url && selectedRequest.request?.status !== "signed" && (
                    <a 
                      href={selectedRequest.request.signing_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1 text-sm"
                    >
                      Open Signing Link <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Customer</h3>
                  <div>
                    <p className="font-medium">{selectedRequest.user?.full_name || "N/A"}</p>
                    <p className="text-sm text-muted-foreground">{selectedRequest.user?.email}</p>
                    <p className="text-sm text-muted-foreground">{selectedRequest.user?.phone || "No phone"}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Account Details</h3>
                  <div>
                    <p className="font-medium">Account #: {selectedRequest.user?.account_number || "N/A"}</p>
                    <p className="text-sm text-muted-foreground">Policy: {selectedRequest.policy?.policy_id}</p>
                  </div>
                </div>
              </div>

              {/* Policy Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Rent</p>
                  <p className="font-medium">${selectedRequest.policy?.monthly_rent?.toFixed(2) || "0.00"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Premium</p>
                  <p className="font-medium">${selectedRequest.policy?.monthly_premium?.toFixed(2) || "0.00"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Coverage</p>
                  <p className="font-medium">{selectedRequest.policy?.coverage_months || 3} months</p>
                </div>
              </div>

              {/* DocuSeal Info */}
              <div className="text-sm text-muted-foreground">
                <p>DocuSeal Submission ID: <code className="bg-muted px-2 py-1 rounded">{selectedRequest.request?.docuseal_submission_id || "N/A"}</code></p>
                {selectedRequest.request?.last_checked && (
                  <p className="mt-1">Last checked: {new Date(selectedRequest.request.last_checked).toLocaleString()}</p>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            {selectedRequest?.request?.status !== "signed" && selectedRequest?.request?.status !== "cancelled" && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => setCancelOpen(true)}
                  data-testid="cancel-request-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Request
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleResend(selectedRequest?.request?.request_id)}
                  disabled={resending === selectedRequest?.request?.request_id}
                  data-testid="resend-request-btn"
                >
                  {resending === selectedRequest?.request?.request_id ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Resend Email
                </Button>
              </>
            )}
            <Button 
              onClick={() => handleCheckStatus(selectedRequest?.request?.request_id)}
              disabled={checkingStatus === selectedRequest?.request?.request_id}
              data-testid="check-status-btn"
            >
              {checkingStatus === selectedRequest?.request?.request_id ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Check Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Signature Request?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this signature request? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              No, Keep It
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Yes, Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
